import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import * as Constants from "config";
import { isEqual } from "lodash";
import Guid from "_shared/guids/guid";
import { getSignalRReconnectDelaysMs } from "_shared/_stores/realtime/signalr-reconnect-policy";
import NotificationSchema from "notifications/entities/notification/notification-schema";
import {
  addNotification,
  removeNotification,
} from "notifications/entities/notification/store/notifications-redux-slice";
import Session from "users/__session/session";
import Notification from "notifications/entities/notification/notification";
import MessageNotification from "notifications/entities/message-notification/message-notification";
import MessageNotificationSchema from "notifications/entities/message-notification/message-notification-schema";
import reduxSignalRMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-signalr-middleware";

const COMMUNICATIONS_INTERFACE_NAME = "communicationsPage";

export const notificationsHubConnection = new HubConnectionBuilder()
  .configureLogging(LogLevel.Debug)
  .withUrl(`${Constants.apiBaseUrl}/notificationHub`, {
    skipNegotiation: true,
    transport: HttpTransportType.WebSockets,
    accessTokenFactory: () => Session.loadFromStorage(() => {}).authToken?.value ?? "",
  })
  .withAutomaticReconnect(getSignalRReconnectDelaysMs())
  .build();

const toNotification = (payload: unknown): Notification | MessageNotification | undefined => {
  // Try the more specific schema first
  if (MessageNotificationSchema.isValidJSON(payload)) {
    return MessageNotification.fromJSON(payload);
  }

  if (NotificationSchema.isValidJSON(payload)) {
    return Notification.fromJSON(payload);
  }

  return undefined;
};

const getVisibleSenderName = (notification: Notification | MessageNotification): string | null => {
  if ("messageInfo" in notification) {
    return notification.messageInfo?.senderName?.value ?? notification.senderName?.value ?? null;
  }

  return notification.senderName?.value ?? null;
};

const getVisibleSeenState = (notification: Notification | MessageNotification, userId?: Guid) => {
  if (!userId) {
    return {
      isSeen: false,
      seenAt: null,
    };
  }

  if (notification.senderUserId?.isEqualTo(userId)) {
    return {
      isSeen: true,
      seenAt: notification.createdDate?.toISOString?.() ?? null,
    };
  }

  const recipient = notification.recipients.find(({ userId: recipientUserId }) => recipientUserId.isEqualTo(userId));
  const seenAt = recipient?.seenAt?.toISOString?.() ?? null;

  return {
    isSeen: recipient?.isSeen ?? seenAt !== null,
    seenAt,
  };
};

const toVisibleNotificationProjection = (notification: Notification | MessageNotification, userId?: Guid) => {
  const senderUserId = notification.senderUserId?.value ?? null;
  const recipientIds = notification.recipients.map(({ userId: recipientUserId }) => recipientUserId.value);
  const isRecipient = !!userId && notification.recipients.some(({ userId: recipientUserId }) => recipientUserId.isEqualTo(userId));
  const seenState = getVisibleSeenState(notification, userId);

  return {
    id: notification.id.value,
    topicCategory: notification.topic?.category ?? null,
    actionRequired: notification.actionRequired ?? null,
    message: notification.message?.value ?? null,
    createdDate: notification.createdDate?.toISOString?.() ?? null,
    senderUserId,
    senderName: getVisibleSenderName(notification),
    recipientIds,
    recipientName: notification.recipientName?.value ?? null,
    userRole: userId ? (senderUserId === userId.value ? "sender" : isRecipient ? "recipient" : "other") : "unknown",
    isSeen: seenState.isSeen,
    seenAt: seenState.seenAt,
  };
};

const toNotificationIssuePlaceholder = (entry: unknown): Notification | MessageNotification | undefined => {
  if (!entry || typeof entry !== "object" || !("issue" in entry)) {
    return undefined;
  }

  const notificationId = getNotificationIdValue((entry as { id?: unknown }).id);
  if (!notificationId) {
    return undefined;
  }

  return {
    id: { value: notificationId },
  } as Notification | MessageNotification;
};

const getNotificationFromResponseEntry = (entry: unknown): Notification | MessageNotification | undefined => {
  if (!entry || typeof entry !== "object") {
    return undefined;
  }

  const notification = toNotification(entry);
  if (notification) {
    return notification;
  }

  return toNotificationIssuePlaceholder(entry);
};

const getNotificationIdValue = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "value" in value) {
    const candidate = (value as { value?: unknown }).value;
    return typeof candidate === "string" ? candidate : undefined;
  }

  return undefined;
};

const getCurrentCommunicationsNotification = (
  state: {
    notifications: {
      interfaceQueries?: {
        lastSuccessfulResponses?: Record<string, { data?: unknown[] }>;
      };
    };
  },
  notificationId: string,
): Notification | MessageNotification | undefined => {
  const response = state.notifications.interfaceQueries?.lastSuccessfulResponses?.[COMMUNICATIONS_INTERFACE_NAME];
  const matchingEntry = response?.data?.find((entry) => {
    if (!entry || typeof entry !== "object" || !("id" in entry)) {
      return false;
    }

    return getNotificationIdValue((entry as { id?: unknown }).id) === notificationId;
  });

  return getNotificationFromResponseEntry(matchingEntry);
};

const callbacks = withCallbacks()
  .add("notification-received", (payload: unknown) => (dispatch) => {
    const notification = toNotification(payload);
    if (notification) {
      dispatch(addNotification(notification));
    }
  })
  .add("notification-updated", (payload: unknown) => (dispatch, getState) => {
    const notification = toNotification(payload);
    if (notification) {
      const currentUserId = Session.loadFromStorage(() => {}).user?.id;
      const state = getState() as {
        notifications: {
          byId: {
            entries: Record<string, unknown>;
          };
          interfaceQueries?: {
            lastSuccessfulResponses?: Record<string, { data?: unknown[] }>;
          };
        };
      };
      const currentCommunicationsNotification = getCurrentCommunicationsNotification(state, notification.id.value);
      const existingNotificationJson = state.notifications.byId.entries[notification.id.value];
      const existingNotification = existingNotificationJson
        ? toNotification(existingNotificationJson)
        : currentCommunicationsNotification;

      if (!currentCommunicationsNotification && !existingNotification) {
        return;
      }

      if (currentCommunicationsNotification) {
        dispatch(addNotification(notification));
        return;
      }

      if (
        existingNotification &&
        isEqual(
          toVisibleNotificationProjection(existingNotification, currentUserId),
          toVisibleNotificationProjection(notification, currentUserId),
        )
      ) {
        return;
      }

      dispatch(addNotification(notification));
    }
  })
  .add("notification-deleted", (payload: unknown) => (dispatch) => {
    const deletedNotificationId = new Guid((payload as { id: string }).id);
    dispatch(removeNotification(deletedNotificationId));
  });

export const notificationsSignalRReduxMiddleware = reduxSignalRMiddleware({
  callbacks,
  connection: notificationsHubConnection,
  connectionHealth: { key: "notifications", label: "Notifications" },
  shouldConnectionStartImmediately: true,
});
